// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { httpsCallable } from 'firebase/functions';

import { functions } from '../../utils/firebase';

const UPDATE_BREWERY_GROUP_CALLABLE =
  'callableBreweryUpdateBreweryGroup-updateBreweryGroup';

function RenameBreweryGroupDialog({
  open,
  groupId,
  initialDisplayName,
  onClose,
  onRenamed,
}) {
  const [displayName, setDisplayName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDisplayName(
        typeof initialDisplayName === 'string' ? initialDisplayName : '',
      );
      setErrorMessage('');
    }
  }, [initialDisplayName, open]);

  const handleDisplayNameChange = useCallback((event) => {
    setDisplayName(event.target.value);
  }, []);

  const handleClose = useCallback(() => {
    if (!isSaving) {
      onClose();
    }
  }, [isSaving, onClose]);

  const handleSave = useCallback(async () => {
    const trimmedGroupId = typeof groupId === 'string' ? groupId.trim() : '';
    const trimmedDisplayName = displayName.trim();

    if (!trimmedGroupId) {
      setErrorMessage('Group id is required.');
      return;
    }
    if (trimmedDisplayName.length === 0) {
      setErrorMessage('Brand display name is required.');
      return;
    }

    const updateCallable = httpsCallable(functions, UPDATE_BREWERY_GROUP_CALLABLE);
    setIsSaving(true);
    setErrorMessage('');

    try {
      await updateCallable({
        groupId: trimmedGroupId,
        displayName: trimmedDisplayName,
      });
      onRenamed();
      onClose();
    } catch (renameError) {
      setErrorMessage(renameError?.message || 'Could not rename brewery group.');
    } finally {
      setIsSaving(false);
    }
  }, [displayName, groupId, onClose, onRenamed]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>Rename brewery group</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Brand display name"
          value={displayName}
          onChange={handleDisplayNameChange}
          error={Boolean(errorMessage)}
          helperText={errorMessage || 'Used for beer search when locations belong to this group.'}
          disabled={isSaving}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RenameBreweryGroupDialog;
